/**
 * WhoIsCallingGate — the reporter-role question (AQ-632), non-blocking by
 * design: candidate confirmation proceeds without it; only the reporter-block
 * fields wait for the answer (R2, scoped to what it protects).
 */
import React from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import type { PmsReporterRole } from '@/hooks/usePmsPrefill';

const ROLES: Array<{ value: PmsReporterRole; label: string }> = [
  { value: 'patient', label: 'Patient' },
  { value: 'hcp', label: 'Healthcare professional' },
  { value: 'caregiver', label: 'Caregiver / family' },
  { value: 'other', label: 'Other' },
];

interface WhoIsCallingGateProps {
  role: PmsReporterRole | null;
  onRoleChange: (role: PmsReporterRole) => void;
  /** Reporter-block fields currently waiting on the answer. */
  awaitingCount: number;
}

export function WhoIsCallingGate({ role, onRoleChange, awaitingCount }: WhoIsCallingGateProps) {
  return (
    <Card>
      <CardContent className="space-y-2 py-3">
        <div className="text-sm font-medium">Who is reporting?</div>
        <div className="flex flex-wrap gap-2">
          {ROLES.map(r => (
            <Button
              key={r.value}
              type="button"
              size="sm"
              variant={role === r.value ? 'default' : 'outline'}
              onClick={() => onRoleChange(r.value)}
              data-analytics-id="case:pms:reporter-role:select"
            >
              {r.label}
            </Button>
          ))}
        </div>
        {role === null && awaitingCount > 0 && (
          <p className="text-xs text-muted-foreground">
            Answer to fill {awaitingCount} reporter field{awaitingCount === 1 ? '' : 's'} from the
            PMS.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
